import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { CaLabeling as ApiCaLabeling, ErrorResponse, Product, ProductUpdate } from '~/Api/User/ProductGroup/CaLabeling';
import config from '~/config';
import { selectCurrency } from '~/Currency';
import { handleError } from '~/ErrorHandler';
import { flashSuccessMessage } from '~/FlashMessage';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
	State as PriceReferencingState,
} from '~/PriceReferencing';
import { PricedProduct, Product as ProductType } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { fetchPrices, pruneAllPrices, selectProductPrice, State as ProductPriceState } from '~/ProductPrice';
import { ThunkDispatch } from '~/Store';

import { Action } from './Action';
import {
	commitCaLabelingUpdate,
	fetchCaLabeling,
	handleCaLabelingUpdateResponse,
	handleCommitError,
	pruneCaLabeling,
	scheduleCaLabelingUpdate,
} from './actions';
import CaLabelingContent from './CaLabeling';
import { selectCaLabeling, State as CaLabelingState } from './reducer';

interface BasketProduct {
	country: Country;
	product: Product;
}
interface BasketOptionalProduct {
	country: Country;
	product: Product | null;
}

const containsCountry = (countries: Country[], code: string): boolean =>
	countries.find((country) => country.alpha2 === code) !== undefined;

const compareReferences = (
	reference: BasketProduct | BasketOptionalProduct,
	otherReference: BasketProduct | BasketOptionalProduct
): number => {
	if (reference.product !== null && otherReference.product === null) {
		return -1;
	}
	if (reference.product === null && otherReference.product !== null) {
		return 1;
	}
	const comparison = reference.country.alpha2.localeCompare(otherReference.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product !== null && otherReference.product !== null) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne((caLabeling: ApiCaLabeling | null, versions: ReferenceBasketVersion[] | null) => {
	const version =
		versions !== null && caLabeling !== null
			? versions.find((version) => version.id === caLabeling.reference.referenceBasketVersion) ?? null
			: null;
	const referenceCountries = version !== null ? version.referenceCountries : [];
	const products = caLabeling !== null && version !== null ? caLabeling.products : [];
	const nonsetCountries = new Set(referenceCountries?.map((country) => country.alpha2));

	const canadianProducts: BasketOptionalProduct[] = [];
	const referencedProducts: BasketOptionalProduct[] = [];
	const otherProducts: BasketProduct[] = [];

	products.forEach((product) => {
		const { country } = product.product.dataset.source;
		if (country.alpha2 === 'CA') {
			canadianProducts.push({ country, product });
			return;
		}
		if (containsCountry(referenceCountries ?? [], country.alpha2)) {
			referencedProducts.push({ country, product });
			nonsetCountries.delete(country.alpha2);
			return;
		}
		otherProducts.push({ country, product });
	});

	if (canadianProducts.length === 0) {
		canadianProducts.push({
			country: new Country('CA', 'Canada'),
			product: null,
		});
	}

	referenceCountries?.forEach((country) => {
		if (nonsetCountries.has(country.alpha2)) {
			referencedProducts.push({ country, product: null });
		}
	});

	canadianProducts.sort(compareReferences);
	referencedProducts.sort(compareReferences);
	otherProducts.sort(compareReferences);

	return {
		canadianProducts,
		otherProducts,
		referenceBasketVersion: version,
		referencedProducts,
	};
});

const createPricedProducts = (
	items: BasketOptionalProduct[],
	state: ProductPriceState,
	currency: string | null,
	priceType: string | null,
	filterCalculatedPrice: boolean,
	groupId: string | null
): (BasketOptionalProduct & { product: PricedProduct<Product> })[] => {
	return items
		.map((item) => ({
			...item,
			product:
				item.product !== null
					? new PricedProduct(
							item.product,
							item.product.product,
							selectProductPrice(
								state,
								item.product.product,
								currency,
								priceType,
								filterCalculatedPrice,
								groupId,
								'CAD'
							)
					  )
					: null,
		}))
		.filter((item): item is BasketOptionalProduct & { product: PricedProduct<Product> } => Boolean(item));
};

const createCanadianPricedProducts = memoizeOne(createPricedProducts);
const createOtherPricedProducts = memoizeOne(createPricedProducts);
const createReferencePricedProducts = memoizeOne(createPricedProducts);

interface State {
	caLabeling: CaLabelingState;
	priceReferencing: PriceReferencingState;
	productPrice: ProductPriceState;
}

interface OwnProps {
	currency: string | null;
	group: UserProductGroup;
	priceType: string | null;
	filterCalculatedPrice: boolean;
}

const mapStateToProps = (state: State, { group, currency, priceType, filterCalculatedPrice }: OwnProps) => {
	const caLabelingState = selectCaLabeling(state.caLabeling, group.id);
	const caLabeling = caLabelingState?.caLabeling ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.caLabeling
	);

	const allowedCurrency =
		currency !== null
			? selectCurrency(group.exchangeRates.availableCurrencies, currency) !== null
				? currency
				: config.product.defaultPriceCurrency
			: null;

	const productGroups = groupProducts(caLabeling, versions);

	const canadianProducts = createCanadianPricedProducts(
		productGroups.canadianProducts,
		state.productPrice,
		allowedCurrency,
		priceType,
		filterCalculatedPrice,
		group.id
	);
	const referencedProducts = createReferencePricedProducts(
		productGroups.referencedProducts,
		state.productPrice,
		allowedCurrency,
		priceType,
		filterCalculatedPrice,
		group.id
	);
	const otherProducts = createOtherPricedProducts(
		productGroups.otherProducts,
		state.productPrice,
		allowedCurrency,
		priceType,
		filterCalculatedPrice,
		group.id
	);

	return {
		canadianProducts,
		otherProducts,
		pricedProducts: [...canadianProducts, ...referencedProducts, ...otherProducts]
			.map((pricedProduct) => pricedProduct.product)
			.filter((product) => product !== null),
		referenceBasketVersion: productGroups.referenceBasketVersion,
		referencedProducts,
		caLabeling,
		isFetching: Boolean(caLabelingState?.isFetching),
		versions,
		isFetchingProductPrices: state.productPrice.request !== null,
	};
};

const actions = {
	onCurrencyChanged:
		(group: UserProductGroup, currency: string | null, pricedProducts: Array<PricedProduct<Product>>) =>
		async (dispatch: ThunkDispatch<Action>) => {
			try {
				if (currency !== 'CAD') {
					await dispatch(
						fetchPrices(
							pricedProducts.map((pricedProduct) => pricedProduct.product.id),
							currency,
							group.id
						)
					);
				}
				dispatch(flashSuccessMessage('Products currency has been changed'));
			} catch (error) {
				dispatch(handleError(error));
			}
		},
	onFetch: (group: UserProductGroup, currency: string | null) => async (dispatch: ThunkDispatch<Action>) => {
		dispatch(fetchReferenceBasketVersions(Module.caLabeling));

		try {
			await dispatch(commitCaLabelingUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}

		const allowedCurrency =
			currency !== null
				? selectCurrency(group.exchangeRates.availableCurrencies, currency) !== null
					? currency
					: config.product.defaultPriceCurrency
				: null;

		try {
			const { caLabeling } = await dispatch(fetchCaLabeling(group.id));
			if (allowedCurrency !== 'CAD' && caLabeling.products.length > 0) {
				await dispatch(
					fetchPrices(
						caLabeling.products.map((product) => product.product.id),
						currency,
						group.id
					)
				);
			}
		} catch (error) {
			if (!(error instanceof ErrorResponse)) {
				dispatch(handleError(error));
				return;
			}
			error.handleProductGroupNotFound();
			dispatch(handleError(error));
		}
	},
	onPrune: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(commitCaLabelingUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}
		dispatch(pruneCaLabeling(group.id));
		dispatch(pruneReferenceBasketVersions());
		dispatch(pruneAllPrices());
		dispatch(pruneDatasets());
	},
	onSave: (group: UserProductGroup) => async (dispatch: ThunkDispatch<Action>) => {
		try {
			await dispatch(commitCaLabelingUpdate(group.id));
		} catch (error) {
			dispatch(handleCaLabelingUpdateResponse(error, group.id));
		}
	},
	onUpdate:
		(group: UserProductGroup, basketVersion: string, products: ProductUpdate[]) =>
		async (dispatch: ThunkDispatch<Action>) => {
			try {
				await dispatch(scheduleCaLabelingUpdate(group.id, basketVersion, products));
			} catch (error) {
				dispatch(handleCaLabelingUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: ProductType[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<Action>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(CaLabelingContent);
